import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { displayToastError } from '@/helpers';
import { deleteOrganization } from '@/modules/common/api';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { LogoutRequestedRootEvent } from '@/router/state/root/component/root-event';
import { useRootActorRef } from '@/router/state/root/util/context/helpers';

interface DeleteOrganizationModalProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export const DeleteOrganizationModal = ({
  open,
  onOpen,
  onClose,
}: DeleteOrganizationModalProps) => {
  const { t } = useTranslation('authenticated.organization');
  const actorRef = useRootActorRef();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmationText = t('deleteOrganization.modal.confirmPlaceholder');

  const handleDelete = async (closeModal: () => void) => {
    if (confirmText !== confirmationText) return;

    setIsDeleting(true);
    try {
      await deleteOrganization();
      closeModal();
      actorRef.send(new LogoutRequestedRootEvent());
    } catch (error) {
      displayToastError((error as Error).message);
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  const isDeleteEnabled = confirmText === confirmationText;

  return (
    <ConfirmationModal
      open={open}
      onOpen={onOpen}
      onClose={handleClose}
      title={t('deleteOrganization.modal.title')}
      message={t('deleteOrganization.modal.description')}
      confirmText={t('deleteOrganization.modal.confirm')}
      cancelText={t('deleteOrganization.modal.cancel')}
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={isDeleting}
      disabled={!isDeleteEnabled}
    >
      <div className="space-y-2">
        <Label htmlFor="confirm-delete">{t('deleteOrganization.modal.confirmLabel')}</Label>
        <Input
          id="confirm-delete"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder={t('deleteOrganization.modal.confirmPlaceholder')}
          disabled={isDeleting}
        />
      </div>
    </ConfirmationModal>
  );
};
